import { useState, useEffect } from "react"
import { useParams, useLocation } from "wouter"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { ArrowRight, Clock, FileText, Users, Target, Shield, CheckCircle } from "lucide-react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"

export default function PublicIntroScreen() {
  const { projectId } = useParams()
  const [, navigate] = useLocation()
  const queryClient = useQueryClient()
  const publicToken = typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('t') : null
  const isPublic = Boolean(publicToken)

  // Persist public token to session storage to survive route transitions
  useEffect(() => {
    try {
      if (publicToken) {
        sessionStorage.setItem('public_token', publicToken)
      }
    } catch {}
  }, [publicToken])

  // Form state
  const [fullName, setFullName] = useState("")
  const [email, setEmail] = useState("")
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  // Email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  const emailValid = email.match(emailRegex)
  const formValid = isPublic ? true : (!!fullName.trim() && !!emailValid)

  // Fetch existing contact data
  const { data: projectData } = useQuery({
    queryKey: [isPublic ? `/api/public/value-assessment/${publicToken}` : `/api/value-assessment/${projectId}`],
    enabled: isPublic ? !!publicToken : !!projectId
  })

  // Load existing contact data if available
  useEffect(() => {
    if (!projectData || typeof projectData !== 'object' || projectData === null) return
    // Prefer stored contact in summary_data
    const sd: any = (projectData as any).summary_data
    const vs = sd?.valueSummary
    const meta = vs?.meta
    const contact = meta?.contact
    if (contact) {
      setFullName(contact.name || "")
      setEmail(contact.email || "")
      setAgreedToTerms(contact.agreedToTerms || false)
      return
    }
    // Fallback to project primary contact if present
    const proj: any = (projectData as any).project
    if (proj) {
      if (proj.primary_contact_name) setFullName(proj.primary_contact_name)
      if (proj.primary_contact_email) setEmail(proj.primary_contact_email)
    }
  }, [projectData])

  // Save contact data mutation
  const saveContactMutation = useMutation({
    mutationFn: async (contactData: { name: string; email: string; agreedToTerms: boolean }) => {
      const currentSummaryData = (projectData && typeof projectData === 'object' && projectData !== null && 'summary_data' in projectData) 
        ? projectData.summary_data : {}
      const currentValueSummary = (currentSummaryData && typeof currentSummaryData === 'object' && 'valueSummary' in currentSummaryData)
        ? currentSummaryData.valueSummary : {}
      const currentMeta = (currentValueSummary && typeof currentValueSummary === 'object' && 'meta' in currentValueSummary)
        ? currentValueSummary.meta : {}
      const endpoint = isPublic ? `/api/public/value-assessment/${publicToken}` : `/api/value-assessment/${projectId}`
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          summary_data: {
            ...(typeof currentSummaryData === 'object' && currentSummaryData !== null ? currentSummaryData : {}),
            valueSummary: {
              ...(typeof currentValueSummary === 'object' && currentValueSummary !== null ? currentValueSummary : {}),
              meta: {
                ...(typeof currentMeta === 'object' && currentMeta !== null ? currentMeta : {}),
                contact: contactData
              }
            }
          }
        })
      })
      if (!response.ok) throw new Error('Failed to save contact data')
      return response.json()
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [isPublic ? `/api/public/value-assessment/${publicToken}` : `/api/value-assessment/${projectId}`] })
      // Navigate to kickoff while preserving token; guards will bypass auth when token is present
      navigate(`/value-assessment/${projectId}/kickoff?public=true${publicToken ? `&t=${publicToken}` : ''}`)
    }
  })

  const handleStartAssessment = async () => {
    if (isPublic) {
      // Skip capture; continue straight to Kickoff preserving token
      navigate(`/value-assessment/${projectId}/kickoff?public=true${publicToken ? `&t=${publicToken}` : ''}`)
      return
    }
    if (!formValid) return
    const contactData = { name: fullName.trim(), email: email.trim(), agreedToTerms }
    saveContactMutation.mutate(contactData)
  }

  const openTermsModal = (e: React.MouseEvent) => {
    e.preventDefault()
    setIsTermsModalOpen(true)
  }

  const closeTermsModal = () => {
    setIsTermsModalOpen(false)
  }

  // Required logging
  console.log("✅ Public Value Assessment Intro Screen loaded");
  console.log("✅ Contact capture and terms consent implemented");

  return (
    <div className="min-h-screen bg-gradient-to-br from-forestBlack via-[#04414D] to-[#334155] text-white">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#4AE2C9]/20 rounded-full mb-6">
            <Target className="w-10 h-10 text-[#4AE2C9]" />
          </div>
          <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-[#FFFFFF] to-primary bg-clip-text text-transparent">
            Start Your Value Assessment
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            This short intake helps us tailor your automation assessment based on your current finance setup.
            It takes just a few minutes and ensures we prepare a relevant, high-impact session for your team.
          </p>
          <ul className="list-none text-center mt-6 max-w-xl mx-auto text-gray-300">
              <li><b>Project Setup</b> – who you are and what you're solving</li>
              <li><b>Strategic Context</b> – pains, goals, and tools</li>
              <li><b>Team Allocation</b> – how your finance team is resourced</li>
          </ul>
        </div>

        {/* Main Content */}
        <div className="max-w-4xl mx-auto space-y-8">
          {/* What to Expect Card */}
          <Card className="bg-forestBlack/50 border-[#334155] backdrop-blur-sm">
            <CardHeader>
              <CardTitle className="text-2xl font-semibold text-white flex items-center gap-3">
                <FileText className="w-6 h-6 text-[#4AE2C9]" />
                What to Expect
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-gray-300 text-lg">
                You'll be guided through <strong>3 short screens</strong> to provide essential information about your organization and current processes. This input will help our consultants prepare for your assessment session.
              </p>
              
              <div className="grid md:grid-cols-3 gap-6 mt-6">
                <div className="flex flex-col items-center text-center p-6 bg-forestBlack/50 rounded-lg border border-[#334155]">
                  <div className="w-12 h-12 bg-sky-400/20 rounded-full flex items-center justify-center mb-3">
                    <span className="text-xl font-bold text-primary">1</span>
                  </div>
                  <h3 className="font-semibold text-white mb-2">Project Kickoff</h3>
                  <p className="text-sm text-gray-400">Basic organization details and current systems</p>
                </div>
                
                <div className="flex flex-col items-center text-center p-6 bg-forestBlack/50 rounded-lg border border-[#334155]">
                  <div className="w-12 h-12 bg-sky-400/20 rounded-full flex items-center justify-center mb-3">
                    <span className="text-xl font-bold text-primary">2</span>
                  </div>
                  <h3 className="font-semibold text-white mb-2">Strategic Context</h3>
                  <p className="text-sm text-gray-400">Current challenges and improvement priorities</p>
                </div>
                
                <div className="flex flex-col items-center text-center p-6 bg-forestBlack/50 rounded-lg border border-[#334155]">
                  <div className="w-12 h-12 bg-sky-400/20 rounded-full flex items-center justify-center mb-3">
                    <span className="text-xl font-bold text-primary">3</span>
                  </div>
                  <h3 className="font-semibold text-white mb-2">Resource Allocation</h3>
                  <p className="text-sm text-gray-400">FTE estimates and team distribution by region</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Contact Info (public view: no inputs) */}
          <Card className="bg-forestBlack/50 border-[#334155] backdrop-blur-sm">
            <CardContent className="p-6">
              <h3 className="text-xl font-semibold text-white mb-2">Contact Information</h3>
              {isPublic ? (
                <p className="text-gray-300">We'll use the project's primary contact{(fullName || email) ? ` (${[fullName, email].filter(Boolean).join(' • ')})` : ''}. Click continue to begin.</p>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label htmlFor="fullName" className="block text-sm text-gray-300">Full Name *</label>
                    <Input id="fullName" placeholder="e.g. Sarah Morgan" value={fullName} onChange={(e) => setFullName(e.target.value)} className="bg-forestBlack/50 border-[#334155] text-white placeholder-gray-400" />
                  </div>
                  <div className="space-y-1.5">
                    <label htmlFor="workEmail" className="block text-sm text-gray-300">Work Email *</label>
                    <Input id="workEmail" type="email" placeholder="e.g. sarah@company.com" value={email} onChange={(e) => setEmail(e.target.value)} className="bg-forestBlack/50 border-[#334155] text-white placeholder-gray-400" />
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Time Required Info */}
          <Card className="bg-forestBlack/50 border-[#334155] backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Clock className="w-6 h-6 text-teal-400" />
                <h3 className="text-xl font-semibold text-white">Time Required</h3>
              </div>
              <p className="text-gray-300">
                <strong>Up to 30 minutes</strong> to complete all three screens. Your progress is automatically saved as you go.
              </p>
            </CardContent>
          </Card>

          {/* CTA */}
          <div className="text-center">
            {!isSubmitted ? (
              <>
                <Button 
                  onClick={handleStartAssessment}
                  disabled={!formValid || saveContactMutation.isPending}
                  size="lg"
                  className="bg-primary text-forestBlack hover:bg-primaryHover text-lg px-8 py-4 h-auto disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPublic ? 'Continue' : (saveContactMutation.isPending ? 'Saving...' : 'Start My Value Assessment')}
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                {!isPublic && (
                  <p className="text-sm text-gray-400 mt-4">Complete the required fields above to begin your assessment.</p>
                )}
              </>
            ) : (
              <div className="text-emerald-300 font-medium">Contact info saved — you can close this tab.</div>
            )}
          </div>
        </div>

        {/* Terms of Use Modal */}
        <Dialog open={isTermsModalOpen} onOpenChange={setIsTermsModalOpen}>
          <DialogContent className="max-w-2xl bg-forestBlack border-[#334155] text-white">
            <DialogHeader>
              <DialogTitle className="text-xl font-semibold text-white">Terms of Use</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 text-sm text-gray-300">
              <p>
                These Value Assessment terms are currently in draft and subject to finalization.
                By proceeding, you consent to the collection and use of your submitted data solely for the purposes of preparing a tailored automation analysis.
              </p>
              <p>
                This may include identifying automation opportunities, estimating time and cost savings, and preparing outputs for internal presentation.
              </p>
              <p>
                We do not share your information outside Redwood without your permission.
                If you'd like us to delete your information at any point, contact us at support@redwood.com.
              </p>
              
              <p className="text-sm font-medium mb-1 text-white">Key areas to be finalized:</p>
              <ul className="list-disc text-sm text-gray-300 ml-6 mb-4 space-y-1">
                <li>✔ What personal or company data is collected</li>
                <li>✔ How Redwood uses your submitted inputs (e.g. for automation benchmarking, value calculation)</li>
                <li>✔ Whether data is stored, for how long, and where</li>
                <li>✔ Data privacy and access rights (e.g. deletion requests)</li>
                <li>✔ Whether Redwood may share anonymized or aggregated data</li>
                <li>✔ Your rights to review and revoke consent</li>
                <li>✔ Any disclaimers or limitations on results or recommendations</li>
                <li>✔ Contact information for privacy-related questions</li>
              </ul>
              
              <p>
                A full legal version of the Terms of Use will be provided here shortly.
              </p>
            </div>
            <div className="flex justify-end mt-6">
              <Button 
                onClick={closeTermsModal}
                className="bg-primary text-forestBlack hover:bg-primaryHover"
              >
                Close
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  )
}
